import type React from 'react'
import { cx } from '@/lib/cx'

export interface TabDef<T extends string> {
  id: T
  label: string
  /** Left out until a real total is known. A zero while it loads reads as a
   *  record with nothing in it. */
  count?: number | undefined
}

/** Real buttons in a tablist, so arrow keys and a screen reader both work. */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
  aside,
}: {
  tabs: TabDef<T>[]
  active: T
  onChange: (id: T) => void
  label: string
  /** Controls belonging to the active tab, on the same row. */
  aside?: React.ReactNode
}) {
  return (
    // Its own card, not fused to the panel below — that seam is what gives the
    // record screen its structure.
    <div className="flex items-center gap-4 rounded-lg border border-line-soft bg-surface px-4">
      <div
        // `overflow-x: auto` makes this a scroll container on *both* axes —
        // `overflow-y: visible` computes to `auto` once the other axis is set.
        // The active tab's underline hangs 1px below its box, so that 1px was
        // enough to render a full-height vertical scrollbar next to the tabs.
        // The padding swallows the overhang; the negative margin gives back the
        // height, so the underline still sits flush on the card's bottom edge.
        className="-mb-px flex min-w-0 flex-1 gap-1 overflow-x-auto overscroll-x-contain pb-px"
        role="tablist"
        aria-label={label}
      >
        {tabs.map((tab) => {
          const selected = tab.id === active
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`panel-${tab.id}`}
              // Underline plus weight plus colour: never colour alone. The
              // underline is flush with the card's bottom edge.
              className={cx(
                'relative min-h-touch px-3 py-4 text-sm whitespace-nowrap transition-colors duration-150 ease-standard',
                selected
                  ? 'font-semibold text-accent after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-accent after:content-[""]'
                  : 'font-medium text-fg-muted hover:text-fg',
              )}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && (
                // A pill, not a trailing digit: "History 4" read as a label
                // ending in a number.
                <span
                  className={cx(
                    'ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-2xs font-semibold tabular-nums',
                    selected ? 'bg-accent-soft text-accent' : 'bg-subtle text-fg-muted',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {aside && <div className="flex flex-none items-center gap-2 py-2">{aside}</div>}
    </div>
  )
}
